/**
 * PrototypeControlCardTemplate
 *
 * Replicates the pdf-service HTML control card design using jsPDF.
 * Layout mirrors the Playwright-rendered HTML template section by section:
 *   Header (logo | title+reportNo | cert) ▸ Company/Owner/Location ▸
 *   Equipment specs ▸ Inspection reference (tested/checklist/certificate) ▸
 *   Remarks (flex-fill) ▸ Legal+StatusCodes ▸ Result row ▸ Signatures ▸ Footer
 */

import { jsPDF } from 'jspdf'
import autoTable from 'jspdf-autotable'
import {
  CONTROL_CARD_CONFIG,
  CONTROL_CARD_TEXT,
  JOB_TYPE_TRANSLATIONS,
} from '../../config'
import type {
  ControlCardData,
  ControlCardEquipment,
  ControlCardInspection,
} from '../../infrastructure'
import { ImageRenderer } from '../../infrastructure'
import { DateFormattingService } from '../../domain'
import { ControlCardTemplateBase, type LogoConfig } from './ControlCardTemplateBase'
import { ReportNumberingService } from '../../../../services/report-numbering.service'

// ─── Local layout constants (mm, matching the HTML template's proportions) ────
const PAGE_W = 210
const PAGE_H = 297
const M_TOP = 8
const M_LR = 12
const M_BOTTOM = 10
const CONTENT_W = PAGE_W - 2 * M_LR // 186 mm
const TABLE_X = M_LR
const BORDER_COLOR: [number, number, number] = [100, 100, 100] // black
const TEXT_COLOR: [number, number, number] = [0, 0, 0]
const LABEL_COLOR: [number, number, number] = [68, 68, 68] // #444
const BORDER_W = 0.1
const LABEL_SIZE = 6.5
const VALUE_SIZE = 8

export interface PrototypeTemplateOptions {
  finalize?: boolean
  inspectionId?: string
  attachmentImage?: string
}

/**
 * Prototype template – faithful jsPDF reproduction of the HTML control card.
 */
export class PrototypeControlCardTemplate extends ControlCardTemplateBase {
  private readonly imageRenderer2: ImageRenderer
  private readonly dateFmt: DateFormattingService

  constructor(config: typeof CONTROL_CARD_CONFIG = CONTROL_CARD_CONFIG) {
    super(config)
    this.imageRenderer2 = new ImageRenderer(config)
    this.dateFmt = new DateFormattingService()
  }

  // ─── Public API ────────────────────────────────────────────────────────────

  async renderComplete(
    data: ControlCardData,
    logos?: LogoConfig,
    options?: PrototypeTemplateOptions,
  ): Promise<jsPDF> {
    const reportNumber = await this.resolveReportNumber(
      data.inspection,
      options?.inspectionId,
      options?.finalize,
    )
    const enriched: ControlCardData = {
      ...data,
      inspection: { ...data.inspection, reportNumber },
    }
    return this.render(enriched, logos, options)
  }

  // ─── Template-method hooks ─────────────────────────────────────────────────

  protected async renderHeader(
    data: ControlCardData,
    logos?: LogoConfig,
  ): Promise<number> {
    const doc = this.pdfRenderer.getDocument()
    let y = M_TOP

    y = await this.drawHeader(doc, data, logos)
    y = this.drawCompanyOwnerLocation(doc, data, y)
    return y
  }

  protected async renderBody(
    data: ControlCardData,
    startY: number,
    _logos?: LogoConfig,
  ): Promise<number> {
    const doc = this.pdfRenderer.getDocument()
    let y = startY

    // Equipment specifications (main + sub)
    y = this.drawEquipmentSpecs(doc, data.mainEquipment, data.subEquipment, y)

    // Force page break if almost no space left after equipment
    const minUsable = 15
    if (PAGE_H - M_BOTTOM - y < minUsable) {
      doc.setDrawColor(...BORDER_COLOR)
      doc.setLineWidth(BORDER_W)
      doc.line(TABLE_X, y, TABLE_X + CONTENT_W, y)
      doc.addPage()
      y = M_TOP
    }

    // Inspection reference (tested acc. to / checklist / report no.)
    y = this.drawInspectionReference(doc, data.inspection, data.mainEquipment, y)

    // Calculate remaining space to stretch remarks
    const fixedAfterRemarks =
      8 +  // legal text
      8 +  // status codes
      18 + // inspection result row
      30 + // signatures
      16   // footer area
    const availableForRemarks = PAGE_H - M_BOTTOM - y - fixedAfterRemarks
    const remarksHeight = Math.max(12, availableForRemarks)

    // Remarks
    y = this.drawRemarks(doc, data.inspection.remarks, y, remarksHeight)

    // Legal text + status codes
    y = this.drawLegalAndStatusCodes(doc, y)

    // Inspection result row
    y = this.drawInspectionResult(doc, data.inspection, data.mainEquipment, y)

    // Signatures
    y = await this.drawSignatures(doc, data.inspection, y)

    return y
  }

  protected async renderFooter(_data: ControlCardData): Promise<void> {
    const doc = this.pdfRenderer.getDocument()
    const pageCount = (doc as any).getNumberOfPages?.() ?? 1
    const centerX = PAGE_W / 2
    const footerY = PAGE_H - M_BOTTOM + 4
    const paginationY = footerY - 4

    for (let p = 1; p <= pageCount; p++) {
      ;(doc as any).setPage?.(p)

      if (pageCount > 1) {
        doc.setFont('helvetica', 'normal')
        doc.setFontSize(5.5)
        doc.setTextColor(...LABEL_COLOR)
        doc.text(`${p}/${pageCount}`, centerX, paginationY, { align: 'center' })
      }

      doc.setFont('helvetica', 'normal')
      doc.setFontSize(5)
      doc.setTextColor(...TEXT_COLOR)
      doc.text(CONTROL_CARD_TEXT.footer, centerX, footerY, { align: 'center' })
    }
  }

  // ─── Section renderers ─────────────────────────────────────────────────────

  /**
   * Header: 3-column layout matching the HTML template header table.
   *   Left 22 %  – company logo / "STM" text
   *   Center 56 % – title, report number, subtitle, order number
   *   Right 22 % – certification logo
   */
  private async drawHeader(
    doc: jsPDF,
    _data: ControlCardData,
    logos?: LogoConfig,
  ): Promise<number> {
    const headerY = M_TOP
    const leftX = TABLE_X
    const centerX = PAGE_W / 2
    const rightX = TABLE_X + CONTENT_W

    // ── Left: company logo or "STM" fallback ──
    if (logos?.company) {
      // 15% smaller than the original 40×12 mm footprint — keeps the logo
      // anchored to the top-left of the header cell.
      await this.imageRenderer2.addLogo(doc, logos.company, {
        x: leftX + 2,
        y: headerY + 2,
        width: 34,
        height: 10.2,
      })
    } else {
      doc.setFont('helvetica', 'bold')
      doc.setFontSize(22)
      doc.setTextColor(...TEXT_COLOR)
      doc.text('STM', leftX + 2, headerY + 8)
      doc.setFontSize(6)
      doc.text('INSPECTION AS', leftX + 2, headerY + 11)
    }

    // ── Center: title block ──
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(12)
    doc.setTextColor(...TEXT_COLOR)
    doc.text(CONTROL_CARD_TEXT.no.title, centerX, headerY + 6, { align: 'center' })

    doc.setFont('helvetica', 'italic')
    doc.setFontSize(7.5)
    doc.text(CONTROL_CARD_TEXT.no.subtitle, centerX, headerY + 11, { align: 'center' })

    // ── Right: certification logo ──
    if (logos?.certification) {
      await this.imageRenderer2.addLogo(doc, logos.certification, {
        x: rightX - 30,
        y: headerY,
        width: 25,
        height: 16,
      })
    }

    return headerY + 20
  }

  /**
   * Company / Owner / Location – 3-column table (mirroring .company-block)
   */
  private drawCompanyOwnerLocation(
    doc: jsPDF,
    data: ControlCardData,
    startY: number,
  ): number {
    const hasLocation = !!data.mainEquipment.location?.trim()
    const hasOrder = !!data.mainEquipment.serviceOrder?.trim()

    const headers: string[] = ['Firma/Company', 'Eier/Owner']
    const values: string[] = ['STM Inspection As', data.mainEquipment.ownerName || '']

    if (hasLocation) {
      headers.push('Lokasjon/Location')
      values.push(data.mainEquipment.location!)
    }
    if (hasOrder) {
      headers.push('Ordrenr./Order No.')
      values.push(data.mainEquipment.serviceOrder!)
    }

    const colCount = headers.length
    const colStyles: Record<number, { cellWidth: number }> = {}
    for (let i = 0; i < colCount; i++) {
      colStyles[i] = { cellWidth: CONTENT_W / colCount }
    }

    autoTable(doc, {
      head: [headers],
      body: [values],
      startY,
      margin: { left: TABLE_X, right: M_LR },
      tableWidth: CONTENT_W,
      theme: 'grid',
      styles: {
        lineColor: BORDER_COLOR,
        lineWidth: BORDER_W,
      },
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: LABEL_COLOR,
        fontStyle: 'normal',
        fontSize: LABEL_SIZE,
        cellPadding: { top: 0.5, right: 2, bottom: 0, left: 2 },
      },
      bodyStyles: {
        textColor: TEXT_COLOR,
        fontSize: VALUE_SIZE,
        valign: 'middle',
        cellPadding: { top: 1, right: 2, bottom: 1, left: 2 },
      },
      columnStyles: colStyles,
    })
    return doc.lastAutoTable.finalY
  }

  /**
   * Equipment specification grid – mirrors .spec-table with left/right border
   * frame and no internal cell borders, 3-column label+value rows.
   */
  private drawEquipmentSpecs(
    doc: jsPDF,
    main: ControlCardEquipment,
    subs: ControlCardEquipment[],
    startY: number,
  ): number {
    let y = startY

    // Draw equipment block for main
    y = this.drawSingleEquipmentBlock(doc, main, y)

    // Draw each sub-equipment
    for (const sub of subs) {
      y = this.drawSingleEquipmentBlock(doc, sub, y)
    }

    return y
  }

  private drawSingleEquipmentBlock(
    doc: jsPDF,
    eq: ControlCardEquipment,
    startY: number,
  ): number {
    const title =
      eq.name || eq.description || `${eq.category || ''} ${eq.equipmentType || ''}`.trim()

    if (eq.specifications.isEmpty()) return startY

    const displayRows = eq.specifications.toDisplayRows(3)
    let y = startY
    const safeBottomY = PAGE_H - M_BOTTOM - 10

    const handlePageBreak = () => {
      // Close current frame
      doc.setDrawColor(...BORDER_COLOR)
      doc.setLineWidth(BORDER_W)
      doc.line(TABLE_X, y, TABLE_X + CONTENT_W, y)
      doc.addPage()
      y = M_TOP
    }

    // Equipment title row
    const titleHeight = 6
    if (y + titleHeight + 9 > safeBottomY) handlePageBreak()

    doc.setDrawColor(...BORDER_COLOR)
    doc.setLineWidth(BORDER_W)
    doc.line(TABLE_X, y, TABLE_X, y + titleHeight)
    doc.line(TABLE_X + CONTENT_W, y, TABLE_X + CONTENT_W, y + titleHeight)

    doc.setFont('helvetica', 'bold')
    doc.setFontSize(8.5)
    doc.setTextColor(...TEXT_COLOR)
    doc.text(title, TABLE_X + 2, y + 4)
    y += titleHeight

    // Specification rows
    const colW = CONTENT_W / 3
    for (const row of displayRows) {
      const rowHeight = 9

      if (y + rowHeight > safeBottomY) handlePageBreak()

      doc.setDrawColor(...BORDER_COLOR)
      doc.setLineWidth(BORDER_W)
      doc.line(TABLE_X, y, TABLE_X, y + rowHeight)
      doc.line(TABLE_X + CONTENT_W, y, TABLE_X + CONTENT_W, y + rowHeight)

      for (let i = 0; i < row.length; i++) {
        const cell = row[i]
        if (!cell || cell.trim() === '') continue

        const [label, value] = cell.split('\n').map((s: string) => s.trim())
        const cellX = TABLE_X + i * colW + 2

        doc.setFont('helvetica', 'normal')
        doc.setFontSize(LABEL_SIZE)
        doc.setTextColor(...LABEL_COLOR)
        doc.text(label || '', cellX, y + 3.5)

        doc.setFont('helvetica', 'normal')
        doc.setFontSize(8.5)
        doc.setTextColor(...TEXT_COLOR)
        doc.text(value || '', cellX, y + 7.5)
      }

      y += rowHeight
    }

    return y
  }

  /**
   * Inspection reference – 3-column table:
   *   Testet/kontrollert iht. (40%) | Sjekkliste/Check list (30%) | Sertifikat/Certificate (30%)
   */
  private drawInspectionReference(
    doc: jsPDF,
    insp: ControlCardInspection,
    main: ControlCardEquipment,
    startY: number,
  ): number {
    // Compose "Testet/kontrollert iht." — the regulatory prefix is always
    // present; the standards portion is the user's picker selection
    // (`controlledAgainst`) when provided, falling back to the equipment's own
    // production standard.
    //
    // The inspection modal seeds `controlledAgainst` with a forskrift-mentioning
    // default ("Forskrift Nr 1357, 1360"), so naively prepending the canonical
    // forskrift prefix here would duplicate it on the PDF. Detect that case
    // and trust the user's text as-is when it already references both
    // 1357 and 1360.
    const FORSKRIFT_PREFIX = 'Forskrift Nr 1357, Forskrift Nr 1360'
    const userStandards = insp.controlledAgainst?.trim() || ''
    const fallbackStandards = this.getProductionStandard(main)?.trim() || ''
    const userAlreadyHasForskrift =
      /1357/.test(userStandards) && /1360/.test(userStandards)

    let testedAgainst: string
    if (userAlreadyHasForskrift) {
      testedAgainst = userStandards
    } else if (userStandards) {
      testedAgainst = `${FORSKRIFT_PREFIX} & ${userStandards}`
    } else if (fallbackStandards) {
      testedAgainst = `${FORSKRIFT_PREFIX} & ${fallbackStandards}`
    } else {
      testedAgainst = FORSKRIFT_PREFIX
    }

    let checklistValue = main.checklist
    if (!checklistValue) {
      const isF = main.category?.toUpperCase().startsWith('F') ?? false
      checklistValue = isF ? 'Offshore lastebærere' : 'Løfteutstyr'
    }

    autoTable(doc, {
      head: [[
        'Testet/kontrollert iht. - tested/inspected acc. to',
        'Sjekkliste/Check list',
        'Rapportnr./Report No.',
      ]],
      body: [[
        testedAgainst,
        checklistValue,
        insp.reportNumber || '',
      ]],
      startY,
      margin: { left: TABLE_X, right: M_LR },
      tableWidth: CONTENT_W,
      theme: 'grid',
      styles: {
        lineColor: BORDER_COLOR,
        lineWidth: BORDER_W,
      },
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: LABEL_COLOR,
        fontStyle: 'normal',
        fontSize: LABEL_SIZE,
        cellPadding: { top: 0.5, right: 2, bottom: 0, left: 2 },
      },
      bodyStyles: {
        textColor: TEXT_COLOR,
        fontSize: VALUE_SIZE,
        valign: 'middle',
        cellPadding: { top: 1, right: 2, bottom: 1, left: 2 },
      },
      columnStyles: {
        0: { cellWidth: CONTENT_W * 0.4 },
        1: { cellWidth: CONTENT_W * 0.3 },
        2: { cellWidth: CONTENT_W * 0.3 },
      },
    })
    return doc.lastAutoTable.finalY
  }

  /**
   * Remarks – bordered box that stretches to fill remaining page space.
   */
  private drawRemarks(
    doc: jsPDF,
    remarks: string | undefined,
    startY: number,
    minHeight: number,
  ): number {
    const x = TABLE_X
    const w = CONTENT_W
    const labelH = 4.5
    const textPadding = 2

    doc.setDrawColor(...BORDER_COLOR)
    doc.setLineWidth(BORDER_W)

    // Left, right, bottom borders (top merges with previous table)
    doc.line(x, startY, x, startY + minHeight)
    doc.line(x + w, startY, x + w, startY + minHeight)
    doc.line(x, startY + minHeight, x + w, startY + minHeight)

    // Label
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(LABEL_SIZE)
    doc.setTextColor(...LABEL_COLOR)
    doc.text('Merknader/Remarks', x + 2, startY + 3)

    // Content
    if (remarks) {
      doc.setFont('helvetica', 'normal')
      doc.setFontSize(VALUE_SIZE)
      doc.setTextColor(...TEXT_COLOR)
      const maxWidth = w - 2 * textPadding - 2
      const lines = doc.splitTextToSize(remarks, maxWidth)
      doc.text(lines, x + textPadding + 1, startY + labelH + 3)
    }

    return startY + minHeight
  }

  /**
   * Legal text + Status codes combined frame (mirrors .legal-status-frame)
   */
  private drawLegalAndStatusCodes(doc: jsPDF, startY: number): number {
    const x = TABLE_X
    const w = CONTENT_W

    // Legal text
    const legalText = CONTROL_CARD_TEXT.no.regulatory
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(4.5)
    doc.setTextColor(51, 51, 51) // #333
    const legalLines = doc.splitTextToSize(legalText, w - 6)
    const legalH = legalLines.length * 1.8 + 2.5

    doc.setDrawColor(...BORDER_COLOR)
    doc.setLineWidth(BORDER_W)
    doc.line(x, startY, x, startY + legalH)
    doc.line(x + w, startY, x + w, startY + legalH)
    doc.line(x, startY + legalH, x + w, startY + legalH) // divider

    doc.text(legalLines, x + w / 2, startY + 2.5, { align: 'center', maxWidth: w - 6 })

    const codesY = startY + legalH
    // Status codes row
    const codesText = CONTROL_CARD_TEXT.no.statusCodes
    doc.setFontSize(LABEL_SIZE)
    doc.setTextColor(...LABEL_COLOR)
    const codesLines = doc.splitTextToSize(codesText, w - 6)
    const lineH = 2.5
    const codesH = codesLines.length * lineH + 3
    const codesTextY = codesY + (codesH - codesLines.length * lineH) / 2 + lineH * 0.4

    doc.line(x, codesY, x, codesY + codesH)
    doc.line(x + w, codesY, x + w, codesY + codesH)

    doc.text(codesLines, x + w / 2, codesTextY, { align: 'center', maxWidth: w - 6 })

    return codesY + codesH
  }

  /**
   * Inspection result – 4-column table:
   *   Kontrolldato (18%) | Kontrolltype (32%) | Status (30%) | Neste kont. (20%)
   */
  private drawInspectionResult(
    doc: jsPDF,
    insp: ControlCardInspection,
    main: ControlCardEquipment,
    startY: number,
  ): number {
    const examDate = this.dateFmt.formatNorwegianDate(
      insp.performedDate || new Date().toISOString(),
    )
    const examType = JOB_TYPE_TRANSLATIONS[insp.jobType] || insp.jobType
    const statusCode = (insp.status || 'OK').toUpperCase()
    const statusDescriptions: Record<string, string> = {
      'OK': 'Godkjent / Approved',
      'C': 'Comment / Approved',
      'MO': 'MO / Approved',
      'RC': 'Pålegg / Recommendation',
      'NC': 'Ikke godkjent / Not approved',
    }
    const statusDesc = statusDescriptions[statusCode] || 'Godkjent / Approved'
    const nextExam = insp.validUntil
      ? this.dateFmt.formatMonthYear(insp.validUntil)
      : this.dateFmt.calculateNextInspectionDate(insp.performedDate, main.category || '')

    autoTable(doc, {
      head: [[
        'Kontrolldato/Exam. Date',
        'Kontrolltype/Type of Examination',
        { content: 'Status', styles: { halign: 'center' } },
        '',
        'Neste kont./Next Exam.',
      ]],
      body: [[examDate, examType, '', statusDesc, nextExam]],
      startY,
      margin: { left: TABLE_X, right: M_LR },
      tableWidth: CONTENT_W,
      theme: 'grid',
      styles: {
        lineColor: BORDER_COLOR,
        lineWidth: BORDER_W,
      },
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: LABEL_COLOR,
        fontStyle: 'normal',
        fontSize: LABEL_SIZE,
        cellPadding: { top: 0.5, right: 2, bottom: 0, left: 2 },
      },
      bodyStyles: {
        textColor: TEXT_COLOR,
        fontSize: VALUE_SIZE,
        fontStyle: 'normal',
        valign: 'middle',
        cellPadding: { top: 1, right: 2, bottom: 1, left: 2 },
      },
      columnStyles: {
        0: { cellWidth: CONTENT_W * 0.18 },
        1: { cellWidth: CONTENT_W * 0.32 },
        2: { cellWidth: CONTENT_W * 0.07, halign: 'center' },
        3: { cellWidth: CONTENT_W * 0.23 },
        4: { cellWidth: CONTENT_W * 0.20 },
      },
      didDrawCell: (data: any) => {
        // Render the status badge as a filled circle with the code centered
        // inside it — a 1:1 port of the in-app StatusIcon (src/components/
        // StatusIcon.tsx) so the PDF matches what users see on screen.
        if (data.section !== 'body' || data.column.index !== 2) return
        this.drawStatusBadge(doc, statusCode, data.cell)
      },
    })
    return doc.lastAutoTable.finalY
  }

  /**
   * Paints the colored status circle in the given cell. Mirrors the
   * `statusConfig` map in `StatusIcon.tsx` so OK stays green, C amber,
   * NC dark red, RC red-orange, MO orange, with 'M' keeping black text
   * on amber. Unknown codes fall through to the gray '?' badge.
   */
  private drawStatusBadge(
    doc: jsPDF,
    code: string,
    cell: { x: number; y: number; width: number; height: number },
  ): void {
    const config: Record<
      string,
      {
        fill: [number, number, number]
        text: [number, number, number]
        label: string
        fontPt: number
      }
    > = {
      OK:      { fill: [0, 176, 80],   text: [255, 255, 255], label: 'OK', fontPt: 6 },
      C:       { fill: [92, 200, 140], text: [255, 255, 255], label: 'C',  fontPt: 6 },
      NC:      { fill: [204, 0, 0],    text: [255, 255, 255], label: 'NC', fontPt: 5.5 },
      RC:      { fill: [255, 51, 0],   text: [255, 255, 255], label: 'RC', fontPt: 5.5 },
      MO:      { fill: [92, 200, 140], text: [255, 255, 255], label: 'MO', fontPt: 5.5 },
      EXP:     { fill: [191, 191, 191], text: [255, 255, 255], label: 'EXP', fontPt: 5 },
      M:       { fill: [255, 192, 0],  text: [0, 0, 0],       label: 'M',  fontPt: 6 },
      MISSING: { fill: [128, 128, 128], text: [255, 255, 255], label: '?',  fontPt: 6 },
      UNKNOWN: { fill: [128, 128, 128], text: [255, 255, 255], label: '?',  fontPt: 6 },
    }
    const c = config[code] || config.UNKNOWN
    const cx = cell.x + cell.width / 2
    const cy = cell.y + cell.height / 2
    // Leave a hair of padding so the circle doesn't touch the cell border.
    const radius = Math.max(1, Math.min(cell.width, cell.height) / 2 - 0.6)

    doc.setFillColor(...c.fill)
    doc.circle(cx, cy, radius, 'F')

    doc.setFont('helvetica', 'bold')
    doc.setFontSize(c.fontPt)
    doc.setTextColor(...c.text)
    doc.text(c.label, cx, cy, { align: 'center', baseline: 'middle' })
    // Restore defaults so subsequent cells aren't affected.
    doc.setTextColor(...TEXT_COLOR)
    doc.setFont('helvetica', 'normal')
  }

  /**
   * Signatures – 2-column table with optional signature images (mirrors .sig-table)
   */
  private async drawSignatures(
    doc: jsPDF,
    insp: ControlCardInspection,
    startY: number,
  ): Promise<number> {
    let leftCell: { x: number; y: number; width: number; height: number } | null = null
    let rightCell: { x: number; y: number; width: number; height: number } | null = null

    autoTable(doc, {
      body: [
        ['Inspector', 'Verifier'],
        ['', ''],
        [insp.performedBy || '', insp.responsiblePerson || ''],
      ],
      startY,
      margin: { left: TABLE_X, right: M_LR },
      tableWidth: CONTENT_W,
      theme: 'grid',
      styles: {
        lineColor: BORDER_COLOR,
        lineWidth: BORDER_W,
        cellPadding: { top: 1, right: 2, bottom: 2, left: 2 },
      },
      bodyStyles: {
        textColor: TEXT_COLOR,
        fontSize: VALUE_SIZE,
        halign: 'center',
      },
      columnStyles: {
        0: { cellWidth: CONTENT_W / 2 },
        1: { cellWidth: CONTENT_W / 2 },
      },
      didParseCell: (data: any) => {
        if (data.row.index === 0) {
          data.cell.styles.fillColor = [255, 255, 255]
          data.cell.styles.textColor = LABEL_COLOR
          data.cell.styles.fontStyle = 'normal'
          data.cell.styles.fontSize = LABEL_SIZE
        }
        if (data.row.index === 1) {
          data.cell.styles.minCellHeight = 18
        }
        if (data.row.index === 2) {
          data.cell.styles.fontStyle = 'normal'
        }
      },
      didDrawCell: (data) => {
        if (
          data.row.index === 1 &&
          data.cell &&
          data.cell.width &&
          data.cell.height
        ) {
          const info = {
            x: data.cell.x,
            y: data.cell.y,
            width: data.cell.width,
            height: data.cell.height,
          }
          if (data.column.index === 0) leftCell = info
          else if (data.column.index === 1) rightCell = info
        }
      },
    })

    // Overlay signature images after table is drawn
    if (leftCell && insp.performedBySignature) {
      await this.imageRenderer2.addSignatureImage(
        doc,
        insp.performedBySignature,
        leftCell,
      )
    }
    if (rightCell && insp.responsiblePersonSignature) {
      await this.imageRenderer2.addSignatureImage(
        doc,
        insp.responsiblePersonSignature,
        rightCell,
      )
    }

    return doc.lastAutoTable.finalY
  }

  // ─── Helpers ───────────────────────────────────────────────────────────────

  private getProductionStandard(eq: ControlCardEquipment): string | null {
    const spec =
      eq.specifications.findByKey('prod_acc_to') ||
      eq.specifications.findByKey('production_standard_id') ||
      eq.specifications.findByKey('standard')
    return spec?.value ?? null
  }

  private async resolveReportNumber(
    insp: ControlCardInspection,
    inspectionId?: string,
    finalize?: boolean,
  ): Promise<string> {
    if (insp.reportNumber) return insp.reportNumber

    if (inspectionId) {
      const existing =
        await ReportNumberingService.getInspectionReportNumber(inspectionId)
      if (existing) return existing
    }

    if (finalize) {
      try {
        return await ReportNumberingService.getNextReportNumber('STMI')
      } catch {
        return 'STMI-ERROR'
      }
    }

    try {
      return await ReportNumberingService.peekNextReportNumber('STMI')
    } catch {
      return 'STMI-PREVIEW'
    }
  }
}
